/**
 * Money on the frontend, in the same integer paise the backend uses (ARCH-002 §1, Subscription Spec §9.1).
 *
 * There is no floating-point arithmetic in this file. Every calculation is on integers, and the only
 * division is by 100 to split rupees from paise — done with `Math.trunc` and a remainder, never with a
 * decimal. JavaScript would otherwise give ₹0.1 + ₹0.2 = ₹0.30000000000000004, which is exactly the class
 * of bug this product cannot ship.
 */

/** Paise in a rupee. */
export const PAISE_PER_RUPEE = 100;

/** Options for {@link formatAmount}. */
export interface FormatAmountOptions {
  /** Show paise. Default: only when there are any. */
  readonly paise?: 'always' | 'never' | 'when-present';
  /** Show the ₹. Default true. */
  readonly symbol?: boolean;
  /** Show a `+` on a positive amount. Default false. */
  readonly signed?: boolean;
}

/**
 * Formats an amount in paise the way an Indian reader expects: ₹1,24,500 — lakh grouping, not thousands.
 *
 * `Intl.NumberFormat('en-IN')` does the grouping, and it is handed a whole number of rupees rather than a
 * fractional amount, so nothing is rounded on the way through.
 */
export function formatAmount(amountMinor: number, options: FormatAmountOptions = {}): string {
  assertInteger(amountMinor);

  const { paise = 'when-present', symbol = true, signed = false } = options;

  const negative = amountMinor < 0;
  const absolute = Math.abs(amountMinor);
  const rupees = Math.trunc(absolute / PAISE_PER_RUPEE);
  const remainder = absolute % PAISE_PER_RUPEE;

  const showPaise = paise === 'always' || (paise === 'when-present' && remainder !== 0);
  const grouped = new Intl.NumberFormat('en-IN', { useGrouping: true }).format(rupees);
  const body = showPaise ? `${grouped}.${String(remainder).padStart(2, '0')}` : grouped;

  const sign = negative ? '-' : signed ? '+' : '';

  return `${sign}${symbol ? '₹' : ''}${body}`;
}

/** Rupees to paise, for input a person typed. Returns null for anything that is not a valid amount. */
export function parseAmount(input: string): number | null {
  // Surrounding whitespace, the symbol and grouping commas go; whitespace *inside* the number does not,
  // because "3 0" is not thirty and reading it as thirty would be a silent misreading of an amount.
  const trimmed = input.trim().replace(/[₹,]/g, '');
  if (trimmed === '' || !/^-?\d*(\.\d{0,2})?$/.test(trimmed)) {
    return null;
  }

  const negative = trimmed.startsWith('-');
  const withoutSign = trimmed.replace('-', '');
  const [rupees = '0', fraction = ''] = withoutSign.split('.');

  if (rupees === '' && fraction === '') {
    return null;
  }

  const paise = Number(rupees || '0') * PAISE_PER_RUPEE + Number(fraction.padEnd(2, '0') || '0');

  return negative ? -paise : paise;
}

/** The whole-rupee part, unsigned. For a display that sets rupees and paise differently. */
export function rupeesPart(amountMinor: number): number {
  assertInteger(amountMinor);
  return Math.trunc(Math.abs(amountMinor) / PAISE_PER_RUPEE);
}

/** The paise part, always 0–99. */
export function paisePart(amountMinor: number): number {
  assertInteger(amountMinor);
  return Math.abs(amountMinor) % PAISE_PER_RUPEE;
}

/**
 * Splits a tax-inclusive total into base and tax, exactly — the same arithmetic as the backend's
 * `Money.SplitInclusive`. The tax is the remainder rather than a second calculation, so
 * `base + tax === total` holds for every amount.
 */
export function splitInclusive(totalMinor: number, percent: number): { base: number; tax: number } {
  assertInteger(totalMinor);

  if (percent === 0) {
    return { base: totalMinor, tax: 0 };
  }

  const divisor = 100 + percent;
  const scaled = totalMinor * 100;
  let base = Math.trunc(scaled / divisor);
  const remainder = scaled % divisor;

  if (Math.abs(remainder) * 2 >= divisor) {
    base += Math.sign(scaled);
  }

  return { base, tax: totalMinor - base };
}

function assertInteger(amountMinor: number): void {
  if (!Number.isSafeInteger(amountMinor)) {
    throw new TypeError(
      `Money is a whole number of paise; received ${amountMinor}. Format at the edge, never calculate in rupees.`,
    );
  }
}
